import { useState } from 'react';
import { Mail, User } from 'lucide-react';
import { FormField, Input, Textarea } from '@/components/ui';
import type { WizardData } from '../types';
import { isValidEmail } from '../utils';

interface StepCustomerProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}

/**
 * Step 1: Customer Data
 * Collects customer email, name, and optional notes. Required-field
 * errors appear once a field has been left; an invalid email is flagged
 * while typing so the disabled "Next" button is never a mystery.
 */
export function StepCustomer({ data, onChange }: StepCustomerProps) {
  const [touched, setTouched] = useState({ email: false, name: false });

  const hasEmailContent = data.customerEmail.trim().length > 0;
  const isEmailInvalid = hasEmailContent && !isValidEmail(data.customerEmail);
  const emailError = isEmailInvalid
    ? 'Please enter a valid email address'
    : touched.email && !hasEmailContent
      ? 'Customer email is required'
      : undefined;

  const nameError =
    touched.name && data.customerName.trim().length === 0
      ? 'Customer name is required'
      : undefined;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField label="Customer Email" required error={emailError}>
          <Input
            icon={Mail}
            type="email"
            placeholder="name@company.com"
            autoComplete="email"
            value={data.customerEmail}
            onChange={(e) => onChange({ customerEmail: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
          />
        </FormField>

        <FormField label="Customer Name / Brand" required error={nameError}>
          <Input
            icon={User}
            type="text"
            placeholder="e.g. ACME Merch"
            autoComplete="off"
            value={data.customerName}
            onChange={(e) => onChange({ customerName: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
          />
        </FormField>
      </div>

      <FormField label="Short Description" hint="Optional">
        <Textarea
          placeholder="e.g. Team wear for our company or community merch..."
          value={data.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </FormField>
    </div>
  );
}
